import { useEffect, useRef, useState } from "react";

const BINANCE_API = "https://api.binance.com/api/v3/ticker/24hr";
const BINANCE_WS = "wss://stream.binance.com:9443/ws";

const useBinance24hTicker = (symbol) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const socketRef = useRef(null);
  const reconnectTimer = useRef(null);
  const activeSymbolRef = useRef(null);

  useEffect(() => {
    if (!symbol) return;

    const upperSymbol = symbol.toUpperCase();
    activeSymbolRef.current = upperSymbol;

    //  RESET OLD DATA
    setData(null);
    setLoading(true);

    // ===============================
    // FIRST → REST API FETCH
    // ===============================
    const fetch24hTicker = async () => {
      try {
        const res = await fetch(`${BINANCE_API}?symbol=${upperSymbol}`);
        const apiData = await res.json();

        //  symbol changed while api loading
        if (activeSymbolRef.current !== upperSymbol) return;

        setData({
          symbol: apiData.symbol,
          lastPrice: apiData.lastPrice,
          livePrice: apiData.lastPrice,
          change24h: apiData.priceChange,
          changePercent24h: apiData.priceChangePercent,
          high24h: apiData.highPrice,
          low24h: apiData.lowPrice,
          volume24h: apiData.volume,
          quoteVolume24h: apiData.quoteVolume,
        });

        setLoading(false);

        // after api success → start socket
        connectSocket();
      } catch (err) {
        console.error("API error", err);
        setLoading(false);
      }
    };

    // ===============================
    //  SOCKET CONNECTION
    // ===============================
    const connectSocket = () => {
      if (socketRef.current) {
        socketRef.current.close();
      }

      const ws = new WebSocket(
        `${BINANCE_WS}/${upperSymbol.toLowerCase()}@ticker`
      );

      socketRef.current = ws;

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);

        // ignore old symbol packets
        if (msg.s !== activeSymbolRef.current) return;

        setData((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            lastPrice: msg.c,
            livePrice: msg.c,
            change24h: msg.p,
            changePercent24h: msg.P,
            high24h: msg.h,
            low24h: msg.l,
            volume24h: msg.v,
            quoteVolume24h: msg.q,
          };
        });
      };

      ws.onerror = () => ws.close();

      ws.onclose = () => {
        // auto reconnect
        reconnectTimer.current = setTimeout(connectSocket, 2000);
      };
    };

    fetch24hTicker();

    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (socketRef.current) socketRef.current.close();
    };
  }, [symbol]);

  return { data, loading };
};

export default useBinance24hTicker;
